import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import JSZip from 'jszip';
import {
    FaCube, FaDownload, FaFileArchive, FaClock, FaCheckCircle,
    FaExclamationTriangle, FaShieldAlt, FaSpinner, FaTooth
} from 'react-icons/fa';
import '../styles/TransferPage.css';

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Mo';
    const mb = bytes / (1024 * 1024);
    if (mb < 0.1) {
        return `${(bytes / 1024).toFixed(1)} Ko`;
    }
    return `${mb.toFixed(1)} Mo`;
};

function TransferPage() {
    const { token } = useParams();
    const [transfer, setTransfer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [zipping, setZipping] = useState(false);
    const [zipProgress, setZipProgress] = useState('');
    const [downloadedNow, setDownloadedNow] = useState(false);

    useEffect(() => {
        const cleanToken = (token || '').trim();
        if (!cleanToken) return;
        fetchTransfer(cleanToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchTransfer = async (tokenToFetch) => {
        const cleanToken = tokenToFetch || (token || '').trim();
        try {
            setLoading(true);
            setError(null);

            let record = null;
            // 1. Try with joined case details
            const { data, error: fetchErr } = await supabase
                .from('case_transfers')
                .select('*, case:cases(case_number, patient_name, patient_reference, practitioner_name)')
                .eq('token', cleanToken)
                .maybeSingle();

            if (!fetchErr && data) {
                record = data;
            } else {
                // 2. Fallback without join (in case RLS on cases restricts anon visitor)
                const { data: rawData, error: rawErr } = await supabase
                    .from('case_transfers')
                    .select('*')
                    .eq('token', cleanToken)
                    .maybeSingle();

                if (rawErr) throw rawErr;
                record = rawData;
            }

            if (!record) {
                setError('not_found');
                return;
            }

            // Check expiration
            if (record.expires_at && new Date() > new Date(record.expires_at)) {
                setError('expired');
                return;
            }

            setTransfer(record);
        } catch (err) {
            console.error('Erreur chargement transfert:', err);
            setError('server_error');
        } finally {
            setLoading(false);
        }
    };

    const trackDownload = async () => {
        if (!transfer?.id) return;
        try {
            const nextCount = (transfer.download_count || 0) + 1;
            const now = new Date().toISOString();
            await supabase
                .from('case_transfers')
                .update({
                    download_count: nextCount,
                    last_downloaded_at: now
                })
                .eq('id', transfer.id);

            setTransfer(prev => ({
                ...prev,
                download_count: nextCount,
                last_downloaded_at: now
            }));
            setDownloadedNow(true);
        } catch (e) {
            console.warn('Track download failed:', e);
        }
    };

    const handleDownloadSingle = (file) => {
        trackDownload();
    };

    const handleDownloadAllZip = async () => {
        const files = transfer?.files || [];
        if (files.length === 0) return;

        try {
            setZipping(true);
            setZipProgress('Préparation des fichiers...');

            const zip = new JSZip();

            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                setZipProgress(`Téléchargement (${i + 1}/${files.length}) : ${f.name}...`);
                const response = await fetch(f.url);
                const blob = await response.blob();
                zip.file(f.name, blob);
            }

            setZipProgress('Création de l\'archive ZIP...');
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            const caseNum = transfer?.case?.case_number || 'transfer';
            const fileName = `TransferEno_${caseNum}.zip`;

            // Trigger browser download
            const link = document.createElement('a');
            link.href = URL.createObjectURL(zipBlob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);

            await trackDownload();
        } catch (err) {
            console.error('Erreur création archive ZIP:', err);
            alert("Erreur lors de la création de l'archive. Vous pouvez télécharger les fichiers individuellement ci-dessous.");
        } finally {
            setZipping(false);
            setZipProgress('');
        }
    };

    if (loading) {
        return (
            <div className="transfer-page-wrapper">
                <div className="transfer-card text-center p-5">
                    <FaSpinner className="spin-icon text-primary mb-3" size={38} />
                    <h4>Chargement du transfert...</h4>
                    <p className="text-muted">Récupération des fichiers 3D en cours.</p>
                </div>
            </div>
        );
    }

    if (error === 'server_error') {
        return (
            <div className="transfer-page-wrapper">
                <div className="transfer-card text-center p-5">
                    <div className="status-icon-circle bg-danger-subtle text-danger mb-3">
                        <FaExclamationTriangle size={32} />
                    </div>
                    <h3 className="fw-bold">Erreur de connexion</h3>
                    <p className="text-muted mb-4">
                        Impossible de récupérer les fichiers du transfert pour le moment.
                    </p>
                    <button onClick={() => fetchTransfer()} className="btn btn-primary px-4 py-2 rounded-3 fw-bold">
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    if (error === 'not_found' || !transfer) {
        return (
            <div className="transfer-page-wrapper">
                <div className="transfer-card text-center p-5">
                    <div className="status-icon-circle bg-danger-subtle text-danger mb-3">
                        <FaExclamationTriangle size={32} />
                    </div>
                    <h3 className="fw-bold">Lien de transfert introuvable</h3>
                    <p className="text-muted mb-4">
                        Ce lien n'existe pas ou a été supprimé par l'administrateur du laboratoire.
                    </p>
                    <Link to="/login" className="btn btn-primary px-4 py-2 rounded-3 fw-bold">
                        Accéder à E-novadent
                    </Link>
                </div>
            </div>
        );
    }

    if (error === 'expired') {
        return (
            <div className="transfer-page-wrapper">
                <div className="transfer-card text-center p-5">
                    <div className="status-icon-circle bg-warning-subtle text-warning mb-3">
                        <FaClock size={32} />
                    </div>
                    <h3 className="fw-bold">Lien de transfert expiré</h3>
                    <p className="text-muted mb-4">
                        La date limite de validité pour ce transfert est dépassée. Veuillez contacter le laboratoire E-novadent pour demander un nouveau lien.
                    </p>
                    <Link to="/login" className="btn btn-outline-secondary px-4 py-2 rounded-3 fw-semibold">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    const files = transfer.files || [];
    const totalBytes = files.reduce((acc, f) => acc + (f.size || 0), 0);
    const caseInfo = transfer.case || {};

    const getRemainingDays = () => {
        if (!transfer.expires_at) return null;
        const diffMs = new Date(transfer.expires_at) - new Date();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const remainingDays = getRemainingDays();

    return (
        <div className="transfer-page-wrapper">
            <div className="transfer-card animate__animated animate__fadeInUp">
                {/* Header Brand */}
                <div className="transfer-header">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                            <div className="brand-logo-badge">
                                <FaTooth size={22} />
                            </div>
                            <div>
                                <h2 className="transfer-brand-title m-0">TransferEno</h2>
                                <span className="transfer-brand-sub">E-NOVADENT • Laboratoire de Prothèse Dentaire</span>
                            </div>
                        </div>
                        <span className="badge bg-success-subtle text-success border border-success-subtle d-flex align-items-center gap-1.5 px-3 py-2 rounded-pill fw-semibold">
                            <FaShieldAlt /> Transfert sécurisé
                        </span>
                    </div>
                </div>

                {/* Case Details Box */}
                <div className="transfer-case-info">
                    <div className="row g-3">
                        <div className="col-sm-6">
                            <span className="case-meta-label">Dossier / Cas</span>
                            <div className="case-meta-value font-monospace">
                                {caseInfo.case_number || 'Dossier prothétique'}
                            </div>
                        </div>
                        {caseInfo.practitioner_name && (
                            <div className="col-sm-6">
                                <span className="case-meta-label">Praticien</span>
                                <div className="case-meta-value">
                                    Dr. {caseInfo.practitioner_name}
                                </div>
                            </div>
                        )}
                        {caseInfo.patient_name && (
                            <div className="col-sm-6">
                                <span className="case-meta-label">Patient</span>
                                <div className="case-meta-value">
                                    {caseInfo.patient_name}
                                </div>
                            </div>
                        )}
                        <div className="col-sm-6">
                            <span className="case-meta-label">Validité</span>
                            <div className="case-meta-value text-muted">
                                {remainingDays !== null ? (
                                    <span className="text-primary fw-semibold">
                                        <FaClock className="me-1" /> Expire dans {remainingDays} jour{remainingDays > 1 ? 's' : ''}
                                    </span>
                                ) : (
                                    <span className="text-success fw-semibold">
                                        <FaCheckCircle className="me-1" /> Lien permanent
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Big Action: Download All */}
                <div className="transfer-cta-section text-center">
                    <button
                        type="button"
                        className="btn btn-primary btn-lg transfer-download-all-btn shadow-lg"
                        onClick={handleDownloadAllZip}
                        disabled={zipping || files.length === 0}
                    >
                        {zipping ? (
                            <>
                                <FaSpinner className="spin-icon me-2" />
                                <span>{zipProgress || 'Préparation du téléchargement...'}</span>
                            </>
                        ) : (
                            <>
                                <FaDownload className="me-2" />
                                <span>Tout télécharger ({formatFileSize(totalBytes)})</span>
                            </>
                        )}
                    </button>
                    <p className="text-muted small mt-2 mb-0">
                        {files.length} fichier{files.length > 1 ? 's' : ''} 3D prêt{files.length > 1 ? 's' : ''} au téléchargement
                    </p>
                    {downloadedNow && (
                        <div className="alert alert-success d-inline-flex align-items-center gap-2 mt-3 py-1.5 px-3 rounded-pill small">
                            <FaCheckCircle /> Téléchargement validé et notifié au laboratoire !
                        </div>
                    )}
                </div>

                {/* Individual Files List */}
                <div className="transfer-files-section">
                    <h5 className="transfer-files-title">
                        Contenu du transfert ({files.length})
                    </h5>
                    <div className="transfer-files-list">
                        {files.map((file, idx) => {
                            const isZip = file.name.endsWith('.zip') || file.name.endsWith('.rar');
                            return (
                                <div key={idx} className="transfer-file-item">
                                    <div className="d-flex align-items-center gap-3 text-truncate">
                                        <div className="file-icon-box">
                                            {isZip ? <FaFileArchive className="text-warning" size={20} /> : <FaCube className="text-primary" size={20} />}
                                        </div>
                                        <div className="text-truncate">
                                            <div className="file-item-name text-truncate" title={file.name}>
                                                {file.name}
                                            </div>
                                            <span className="file-item-size text-muted">
                                                {formatFileSize(file.size)}
                                            </span>
                                        </div>
                                    </div>

                                    <a
                                        href={file.url}
                                        download={file.name}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1.5 px-3 py-2 rounded-2 fw-semibold"
                                        onClick={() => handleDownloadSingle(file)}
                                    >
                                        <FaDownload size={13} />
                                        <span>Télécharger</span>
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="transfer-footer text-center">
                    <p className="m-0 text-muted small">
                        TransferEno • Service exclusif développé pour <strong>E-novadent Laboratoire</strong>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default TransferPage;
